import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, ShieldCheck, Sparkles } from 'lucide-react';
import ScoreRingCard from './visuals/ScoreRingCard';
import { stats } from '../../data/mock';

/**
 * Hero - the promise, the proof, and one obvious next step.
 *
 * The headline leads with the outcome (a score you can prove) rather than the
 * technology, and the product visual sits immediately beside it so the claim is
 * evidenced before the fold ends.
 */
const HeroSection = () => {
  return (
    <section className="hero">
      <div className="mx-auto max-w-[1180px]">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          {/* ---------------------------- Copy ---------------------------- */}
          <div className="text-center lg:text-left">
            <div
              className="pill animate-fade-in-up mx-auto lg:mx-0"
              style={{ animationDelay: '0.05s' }}
            >
              <span
                className="grid h-5 w-5 place-items-center rounded-full"
                style={{ background: 'rgba(52,89,164,0.12)' }}
              >
                <Sparkles className="h-3 w-3" style={{ color: 'var(--tenses-blue)' }} />
              </span>
              Now with an AI interview simulator
            </div>

            <h1
              className="heading-1 animate-fade-in-up mt-6"
              style={{ animationDelay: '0.12s' }}
            >
              English proficiency
              <br />
              you can{' '}
              <span className="underline-swash text-gradient">
                actually prove
                <svg viewBox="0 0 300 12" fill="none" preserveAspectRatio="none" aria-hidden="true">
                  <path
                    d="M2 8.5C48 3.5 105 1.8 150 2.6c45 .8 100 2.7 148 6.4"
                    stroke="var(--accent-strong)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    opacity="0.55"
                  />
                </svg>
              </span>
            </h1>

            <p
              className="body-large animate-fade-in-up mx-auto mt-7 max-w-[540px] lg:mx-0"
              style={{ animationDelay: '0.2s' }}
            >
              Practise speaking and writing against AI that scores every attempt, rehearse
              real interviews, and finish with a certified TPI score employers understand.
            </p>

            <div
              className="animate-fade-in-up mt-9 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start"
              style={{ animationDelay: '0.28s' }}
            >
              <a href="https://app.tenses.ai" className="btn-primary">
                Get started
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/tpi" className="btn-secondary">
                <Play className="h-4 w-4" style={{ color: 'var(--tenses-blue)' }} />
                See how TPI works
              </Link>
            </div>

            <div
              className="animate-fade-in-up mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 lg:justify-start"
              style={{ animationDelay: '0.34s' }}
            >
              <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <ShieldCheck className="h-4 w-4" style={{ color: 'var(--accent-strong)' }} />
                Plans from ₹1,500
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <ShieldCheck className="h-4 w-4" style={{ color: 'var(--accent-strong)' }} />
                Instant access
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <ShieldCheck className="h-4 w-4" style={{ color: 'var(--accent-strong)' }} />
                All 11 modules included
              </span>
            </div>
          </div>

          {/* --------------------------- Visual --------------------------- */}
          <div className="animate-fade-in-up" style={{ animationDelay: '0.22s' }}>
            <ScoreRingCard />
          </div>
        </div>

        {/* ----------------------------- Stats ---------------------------- */}
        <div
          className="animate-fade-in-up mt-16 lg:mt-24"
          style={{ animationDelay: '0.42s' }}
        >
          <hr className="rule-fade" />
          <dl className="grid grid-cols-3 gap-6 py-10">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <dd className="stat-value">{s.value}</dd>
                <dt className="stat-label">{s.label}</dt>
              </div>
            ))}
          </dl>
          <hr className="rule-fade" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
